import { TFunction } from 'next-i18next'

// Libs
import { handleGtmClickAds, handleGtmClickCustomService, handleGtmClickNavigation } from '@/methods/handleGtmEvent'
import localizePath from '@/methods/url/localizePath'
import { toCamelCase } from '@/utils/to-case'

// Constants
import { DEFAULT_LOCALE } from '@/constants/defaultLocale'
import { REGION } from '@/constants/region'
import { GTM_LOCATION, GtmClickAdsClickText, GtmNavigationParams } from '@/constants/gtmEvent'

// Types
import type { SiteNavLink } from '@/api/fetchSiteNav'

/** 後台「顯示地區」的其他地區：不在 REGION 營運市場內的國家 */
const REGION_OTHER = 'OTHER'

const KNOWN_REGIONS = new Set<string>(Object.values(REGION))

/**
 * 選單名稱：該語系 → 非中文語系退到 en-us → 預設名稱（繁中）。
 * labels 的 key 經過 API 回應轉成 camelCase（en-us → enUs），這裡用同一個轉換查。
 */
export const pickSiteNavLabel = (item: { label: string; labels?: Record<string, string> }, locale?: string) => {
  const lang = !locale || locale === 'default' ? DEFAULT_LOCALE : locale
  const labels = item.labels ?? {}
  const exact = labels[toCamelCase(lang)]
  if (exact) return exact
  if (!lang.startsWith('zh')) {
    const english = labels[toCamelCase('en-us')]
    if (english) return english
  }
  return item.label
}

/** 顯示地區：空 = 全部；OTHER = 不在營運市場清單內的國家 */
export const isSiteNavVisibleInRegion = (regions: string[] | undefined, countryCode: string) => {
  if (!regions || regions.length === 0) return true
  if (regions.includes(countryCode)) return true
  return regions.includes(REGION_OTHER) && !KNOWN_REGIONS.has(countryCode)
}

/** 站內路徑補語系（新分頁 / 原生 <a> 不會自己帶） */
export const resolveSiteNavUrl = (url: string, locale?: string) => localizePath(url, locale)

/** 點擊追蹤：代號對應既有的 GTM 事件（後台「追蹤事件」選項） */
export const fireSiteNavGtm = (gtmEvent: SiteNavLink['gtmEvent'], t: TFunction) => {
  switch (gtmEvent) {
    case 'faq':
      handleGtmClickNavigation({ clickText: t(GtmNavigationParams.FAQ, { lng: 'zh-tw' }) as GtmNavigationParams })
      break
    case 'customer_service':
      handleGtmClickCustomService({ location: GTM_LOCATION.NAVIGATION })
      break
    case 'terms_of_service':
      handleGtmClickNavigation({ clickText: t(GtmNavigationParams.TERMS_OF_SERVICE, { lng: 'zh-tw' }) as GtmNavigationParams })
      break
    case 'return_policy':
      handleGtmClickNavigation({ clickText: t(GtmNavigationParams.RETURN_POLICY, { lng: 'zh-tw' }) as GtmNavigationParams })
      break
    case 'recruitment_merchant':
      handleGtmClickAds({ clickText: t(GtmClickAdsClickText.RECRUITMENT_MERCHANT, { lng: 'zh-tw' }) })
      break
    case 'recruitment_kol':
      handleGtmClickAds({ clickText: t(GtmClickAdsClickText.RECRUITMENT_KOL, { lng: 'zh-tw' }) })
      break
    default:
      break
  }
}
